// src/pages/Contact.jsx
import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { Send, X } from "lucide-react";
import { ensureAuthOrRedirect, attachExpiryTimer, redirectToLogin } from "../utils/auth";

const CONTACT_API = "https://whiteintel.io/api/regular/app/v2/contact/contact.php";

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

const CATEGORIES = [
  "Pricing Offer",
  "Subscription Upgrade",
  "Technical Issues",
  "Billing",
];

// map label -> API slug (your sample shows lower/underscore like "other")
const toSlug = (label) => label.toLowerCase().replace(/\s+/g, "_");

export default function ContactPage() {
  // layout
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // form
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  // status
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    ensureAuthOrRedirect();
    attachExpiryTimer();
  }, []);

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  const disableSubmit =
    sending || !category || !title.trim() || !message.trim();

  async function handleSubmit(e) {
    e.preventDefault();
    if (disableSubmit) return;

    setSending(true);
    setError("");
    setSuccess("");

    try {
      const res = await fetch(CONTACT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({
          category: toSlug(category), // e.g. "subscription_upgrade"
          title: title.trim(),
          message: message.trim(),
        }),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          msg = j?.error || j?.message || msg;
        } catch {}
        throw new Error(msg);
      }

      setSuccess("Thanks! Your message has been sent. We’ll get back to you shortly.");
      setTitle("");
      setMessage("");
    } catch (err) {
      setError(err.message || "Failed to send your request. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">Contact</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="contact"
          onChange={() => {}}
        />

        <main className="flex-1 px-4 md:px-6 pt-4 pb-10">
          {/* banners */}
          {error && (
            <div className="max-w-3xl mb-4">
              <div className="rounded-md border border-red-500/30 bg-red-500/10 backdrop-blur-sm px-3 py-2 text-sm text-red-200 flex items-start justify-between">
                <span className="pr-4">{error}</span>
                <button
                  onClick={() => setError("")}
                  className="ml-3 p-1 rounded hover:bg-white/5"
                  aria-label="Dismiss error"
                >
                  <X className="h-4 w-4 text-neutral-300" />
                </button>
              </div>
            </div>
          )}

          {success && (
            <div className="max-w-3xl mb-4">
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-sm px-3 py-2 text-sm text-emerald-200 flex items-start justify-between">
                <span className="pr-4">{success}</span>
                <button
                  onClick={() => setSuccess("")}
                  className="ml-3 p-1 rounded hover:bg-white/5"
                  aria-label="Dismiss success"
                >
                  <X className="h-4 w-4 text-neutral-300" />
                </button>
              </div>
            </div>
          )}

          {/* form card */}
          <div className={`max-w-3xl rounded-md ${frosted} p-6`}>
            <p className="text-neutral-300 text-sm mb-6">
              Send us a note and we’ll respond by email. Pick a category so we can route your request quickly.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">
                  Category
                </div>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full h-11 rounded-lg border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                >
                  {CATEGORIES.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </section>

              {/* Title */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">
                  Request Title
                </div>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Briefly describe your request"
                  className="w-full h-11 rounded-lg border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none placeholder:text-neutral-500 focus-visible:ring-2 focus-visible:ring-sky-500/40"
                />
              </section>

              {/* Message */}
              <section>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">
                  Request
                </div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share details so we can help quickly…"
                  rows={8}
                  className="w-full rounded-lg border border-sky-900/30 bg-[#0a1224]/80 px-3 py-2 text-sm text-white outline-none placeholder:text-neutral-500 focus-visible:ring-2 focus-visible:ring-sky-500/40"
                />
              </section>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setCategory(CATEGORIES[0]);
                    setTitle("");
                    setMessage("");
                  }}
                  className="h-10 px-4 rounded-lg border border-neutral-700 text-neutral-200 hover:bg-white/5"
                  disabled={sending}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={disableSubmit}
                  className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg border ${
                    disableSubmit
                      ? "opacity-60 cursor-not-allowed border-sky-600/20 bg-sky-500/5 text-sky-300"
                      : "border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                  }`}
                >
                  <Send className="h-4 w-4" />
                  {sending ? "Sending…" : "Send Request"}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
